import React from 'react';
import { Link, Outlet } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import CenterLoadingSpinner from '../../components/LoadingSpinner/CenterLoadingSpinner';
import { useBrandContext } from '../../contexts/BrandContext';
import { useAPI } from '../../libs/libAPI';
import { getIconForService } from '../../libs/libIcon';
import { titleCase } from '../../libs/libString';
import { sortBy } from '../../libs/utils';
import WDProduct, { WDProductSchema } from '../../types/WDProduct';

const DataSheets: React.FC = () => {
  const api = useAPI();
  const brandContext = useBrandContext();

  const availableApps = brandContext.brand.apps ?? [];

  const productsQuery = useQuery(['products'], async () => {
    const response = await api.fetch(`${import.meta.env.VITE_API_URI}/v1/product-catalog/apps`, { method: 'GET' });

    const products = await WDProductSchema.array().parseAsync(await response.json());

    const apps = products.filter(p => p.showInCatalog).sort(sortBy('name')).sort(sortBy('category'));

    const filteredApps = availableApps.length === 0 ? apps : apps.filter((a) => availableApps.includes(a.name));

    const appsByCategory: { [key: string]: WDProduct[] } = filteredApps.reduce((r, a) => {
      if (!Object.keys(r).includes(a.category)) {
        r[a.category] = [];
      }
      r[a.category].push(a);
      return r;
    }, Object.create(null));

    return appsByCategory;
  });

  if (productsQuery.data == null) return <CenterLoadingSpinner />;

  return <>
    <div>
      <div className='max-w-[100vw] px-12 xl:px--0 3xl:max-w-screen-2xl 2xl:max-w-screen-xl xl:max-w-screen-lg mx-auto'>
        <div>
          <h2 className='text-2xl mt-4 mb-8'>DataSheets</h2>
        </div>
        <div className='flex flex-wrap w-full'>
          {Object.keys(productsQuery.data).map((category) => (
            <div key={category} className='w-full md:w-1/4 md:px-4'>
              <div className='mx-2 my-2 bg-hover rounded-t-xl py-4 px-4 text-center'>
                {titleCase(category)}
              </div>
              {productsQuery.data[category].map((app) => (
                <Link key={app.name} to={`${app.name}`}>
                  <div className={`mx-2 my-4 bg-background2 rounded-xl py-4 px-4 shadow shadow-background2-darker ${app.hasDatasheet ? 'border border-primary' : ''}`}>
                    <div className='flex'>
                      <div>
                        {getIconForService(app.name, 'text-3xl', 'h-8 w-8')}
                      </div>
                      <div className='pl-3 my-auto'>{app.name}</div>
                    </div>
                    <div className='pt-3 opacity-80 text-sm'>
                      {app.description}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
    <Outlet />
  </>;
};

export default DataSheets;
